/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Empty, Row, Spin, Typography } from 'antd';
import FlowGraph from '../Graphs/FlowGraph';
import ConflictChangesModal from '../Modals/ConflictChangesModal';

// Misc imports
import { chaptersourceToFlowGraph, flowGraphToChaptersource } from '../../utils/transform/flowGraph';
import { useCreateActivityMutation, useFetchActivityQuery, useUpdateActivityMutation } from '../../store/activities/activitiesApi';
import { toggleFullScreenMode } from '../../store/global/globalActions';
import { DEFAULT_GRAPH_LAYOUT, NODE_TYPES } from '../../constants/flowGraph';
import { ACTIVITY_TYPES } from '../../constants/main';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import ActivityGraphSettingsForm from '../Forms/ActivityGraphSettingsForm';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';
import { OPTIMISTIC_LOCKING_EXCEPTION_ERROR_CODE } from '../../constants/errors';

// Constant declarations
const { Text } = Typography;

//#region External utils

/**
 * Calculate which nodes are not available for a graph
 * @param {Object} activity The activity object
 * @returns An array of the node types that should not be displayed in the "Add new node" panels
 */
const useDisabledNodeTypes = (activity) => {
  let disabledNodeTypes = [];

  if (!activity) {
    return disabledNodeTypes;
  }

  // Disable the legacy and NLP flow-specific nodes in all cases
  disabledNodeTypes.push(NODE_TYPES.QUERY, NODE_TYPES.PIPELINE, NODE_TYPES.SWITCH_CASE, NODE_TYPES.SWITCH_VARIABLE, NODE_TYPES.LIVECHAT, NODE_TYPES.LINK_OPTION);

  switch (activity.type) {
    case ACTIVITY_TYPES.DECISION_TREE:
    case ACTIVITY_TYPES.INTERVIEW:
    case ACTIVITY_TYPES.AUTOMATED_ANSWERS:
      disabledNodeTypes.push(NODE_TYPES.LIVECHAT, NODE_TYPES.LIVECHAT_REQUEST);
      break;

    case ACTIVITY_TYPES.USER_FEEDBACK:
      disabledNodeTypes.push(NODE_TYPES.LIVECHAT, NODE_TYPES.LIVECHAT_REQUEST, NODE_TYPES.JUMP);
      break;

    default:
      break;
  }

  return disabledNodeTypes;
};

//#endregion

const ActivityFlowEditor = props => {
  const { activityId, bot = {}, isViewOnlyMode = false, injectedNodeData = {} } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const { currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const [searchParams] = useSearchParams();

  const { currentData: activity, isLoading: isFetchingActivity, error: fetchActivityError } = useFetchActivityQuery({
    orgId, tenantId: bot.id ? bot.id : tenantId,
    activityId,
    ...(searchParams.get('token') && { token: searchParams.get('token') }),
    currentBotLanguage
  }, { skip: !activityId });
  const [, { isLoading: isCreatingActivity }] = useCreateActivityMutation({ fixedCacheKey: 'create-activity-shared' });

  const initialGraphElements = useMemo(() => {
    return chaptersourceToFlowGraph(activity, primaryBotLanguage) || [];
  }, [activity, primaryBotLanguage]);

  const initialGraphDirection = useMemo(() => {
    return activity?.content?.layout || DEFAULT_GRAPH_LAYOUT;
  }, [activity]);

  const disabledNodeTypes = useDisabledNodeTypes(activity);
  const [updateActivity, { isLoading: isUpdatingActivity }] = useUpdateActivityMutation({ fixedCacheKey: 'flowGraph-update-activity' });

  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // On mount
  useEffect(() => {
    // Cleanup
    return () => {
      dispatch(toggleFullScreenMode(false));
    };
  }, []);

  // When there's a fetch error, navigate the user back to the activities list
  useEffect(() => {
    // If the graph is not available, remove the activity id from the url
    // This will navigate the user back to the activities list (check ActivitiesView.jsx)
    if (!initialGraphElements && !isFetchingActivity && fetchActivityError === true) {
      const urlParams = new URLSearchParams(location.search);
      if (urlParams.has('activity')) {
        urlParams.delete('activity');
        navigate({
          pathname: location.pathname,
          search: urlParams.toString()
        }, { replace: true });
      }
    }
  }, [fetchActivityError]);

  const onSaveElements = async (elements, { forceOverwrite = false, locationToNavigate = null } = {}) => {
    try {
      const transformedData = flowGraphToChaptersource(elements, { ...activity?.content?.settings }, activity?.content?.layout, primaryBotLanguage);

      const payload = {
        version: activity?.version,
        ...transformedData,
        ...(forceOverwrite && { forceOverwrite: true })
      };
      const isSaveSuccessful = await updateActivity({ orgId, tenantId, activityId, data: payload, errorMessage: { showMessage: false } }).unwrap();
      return isSaveSuccessful;
    } catch (error) {
      if (error?.code === OPTIMISTIC_LOCKING_EXCEPTION_ERROR_CODE) {

        // Location to navigate iset and used when
        // we bypass the prompt and return the location
        // to successfully navigate after conflict resolution
        if (locationToNavigate?.pathname) {
          setPendingLocation(locationToNavigate);
        } else {
          setPendingLocation(null);
        }

        setConflictData({
          metadata: error.metadata,
          elementsToSave: elements
        });
        setIsConflictModalOpen(true);
      } else {
        const errorMessage = error?.details || t('An unknown error occurred.');
        openConsoleAlertMessage({ type: 'error', message: errorMessage });
      }
      return { error };
    }
  };

  const handleCancelConflict = () => {
    setIsConflictModalOpen(false);
    setConflictData(null);
    setPendingLocation(null);
  };

  const handleOverwriteSave = async () => {
    if (conflictData?.elementsToSave) {
      const result = await onSaveElements(conflictData.elementsToSave, { forceOverwrite: true });

      if (result && !result.error && pendingLocation) {
        setHasPendingChanges(false);
        const to = pendingLocation.pathname + pendingLocation.search;
        setPendingLocation(null);
        setIsConflictModalOpen(false);
        setConflictData(null);
        navigate(to, { replace: true });
        return;
      }
    }

    setHasPendingChanges(false);
    setIsConflictModalOpen(false);
    setConflictData(null);
    setPendingLocation(null);
  };

  return (
    isFetchingActivity || isCreatingActivity ? (
      <Row className="w-100">
        <Col flex="auto" className="text-center">
          <Spin className="mt-10 mb-8"/>
        </Col>
      </Row>
    ) : (
      activity && initialGraphElements ? (
        <Fragment>
          <FlowGraph
            graphId={activity.id}
            initialGraphElements={initialGraphElements}
            isLoading={isFetchingActivity || isCreatingActivity || isUpdatingActivity}
            isViewOnlyMode={isViewOnlyMode}
            injectedNodeData={injectedNodeData}
            disabledNodeTypes={disabledNodeTypes}
            onUpdate={onSaveElements}
            hasPendingChanges={hasPendingChanges}
            setHasPendingChanges={setHasPendingChanges}
            initialGraphDirection={initialGraphDirection}
            graphSettingsForm={<ActivityGraphSettingsForm />}
          />

          {conflictData && (
            <ConflictChangesModal
              isOpen={isConflictModalOpen}
              onCancel={handleCancelConflict}
              onOverwrite={handleOverwriteSave}
              conflictInfo={conflictData.metadata}
            />
          )}
        </Fragment>
      ) : (
        <Empty
          description={<Text type="secondary">{t('This flow is not available, please go back to the list to create one')}.</Text>}
        />
      )
    )
  );
};

ActivityFlowEditor.propTypes = {
  activityId: PropTypes.string,
  bot: PropTypes.object,
  isViewOnlyMode: PropTypes.bool,
  injectedNodeData: PropTypes.object
};

export default ActivityFlowEditor;
